import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDownTrayIcon, ChartBarSquareIcon } from '@heroicons/react/24/outline';
import api from '../api/axios';
import { OPERATIONAL_ACADEMIC_YEAR } from '../config/operationalAcademicContext';

const CRITERIA = [
  ['Teaching quality', 'avg_teaching_quality'],
  ['Subject knowledge', 'avg_subject_knowledge'],
  ['Communication', 'avg_communication'],
  ['Punctuality', 'avg_punctuality'],
  ['Doubt resolution', 'avg_doubt_resolution'],
  ['Course coverage', 'avg_course_coverage'],
  ['Assessment fairness', 'avg_assessment_fairness'],
];

const EMPTY_DATA = { cycle: null, faculties: [], selectedFaculty: null, subjects: [] };
const rating = (value) => (value == null ? '-' : Number(value).toFixed(2));

export default function FacultyFeedbackCyclePanel({ instituteId, isSuperAdmin, semesters }) {
  const semesterOptions = useMemo(
    () => Array.from(new Set((semesters || []).map(Number).filter(Boolean))).sort((a, b) => a - b),
    [semesters]
  );
  const [semester, setSemester] = useState('');
  const [facultyId, setFacultyId] = useState('');
  const [data, setData] = useState(EMPTY_DATA);
  const [report, setReport] = useState(null);
  const [loadingFaculty, setLoadingFaculty] = useState(false);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (semesterOptions.length === 0) {
      setSemester('');
      setData(EMPTY_DATA);
      return;
    }
    if (!semesterOptions.includes(Number(semester))) setSemester(String(semesterOptions[0]));
  }, [semester, semesterOptions]);

  const listParams = useMemo(() => {
    const params = {
      semester: Number(semester),
      academicYear: OPERATIONAL_ACADEMIC_YEAR,
    };
    if (isSuperAdmin && instituteId) params.instituteId = instituteId;
    return params;
  }, [instituteId, isSuperAdmin, semester]);

  const detailParams = useMemo(() => {
    if (!facultyId) return null;
    return { ...listParams, facultyId };
  }, [facultyId, listParams]);

  useEffect(() => {
    if (!instituteId || !semester) return;
    let cancelled = false;
    setLoadingFaculty(true);
    setFacultyId('');
    setReport(null);
    setError('');

    api.get('/admin/reports/faculty-cycle', { params: listParams })
      .then((response) => {
        if (!cancelled) setData(response.data?.data || EMPTY_DATA);
      })
      .catch((requestError) => {
        if (!cancelled) {
          setData(EMPTY_DATA);
          setError(requestError?.response?.data?.error?.message || 'Unable to load faculty allocations.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFaculty(false);
      });

    return () => { cancelled = true; };
  }, [instituteId, semester, listParams]);

  const viewFeedback = useCallback(async () => {
    if (!detailParams || !instituteId) return;
    setLoadingFeedback(true);
    setReport(null);
    setError('');
    try {
      const response = await api.get('/admin/reports/faculty-cycle', { params: detailParams });
      setData(response.data?.data || EMPTY_DATA);
    } catch (requestError) {
      setError(requestError?.response?.data?.error?.message || 'Unable to load faculty feedback.');
    } finally {
      setLoadingFeedback(false);
    }
  }, [detailParams, instituteId]);

  useEffect(() => {
    if (!detailParams || !instituteId) return;
    viewFeedback();
  }, [detailParams, instituteId, viewFeedback]);

  const cycle = data.cycle;
  const selectedFaculty = data.selectedFaculty;
  const subjects = Array.isArray(data.subjects) ? data.subjects : [];

  const facultyOptions = useMemo(() => {
    const byId = new Map();
    (data.faculties || []).forEach((faculty) => {
      if (faculty?.id && !byId.has(faculty.id)) byId.set(faculty.id, faculty);
    });
    return Array.from(byId.values()).sort((a, b) => {
      const na = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      if (na !== 0) return na;
      return String(a.employee_id || '').localeCompare(String(b.employee_id || ''));
    });
  }, [data.faculties]);

  const facultyLabel = (faculty) => {
    const name = (faculty.name || '').trim() || 'Faculty';
    const emp = faculty.employee_id ? String(faculty.employee_id).trim() : '';
    return emp ? `${name} (${emp})` : name;
  };

  const downloadFacultyFeedbackPdf = async (subjectIdOptional = null) => {
    if (!facultyId || !semester || !instituteId) return;
    setPdfDownloading(true);
    setError('');
    try {
      const params = {
        semester: Number(semester),
        facultyId,
        academicYear: OPERATIONAL_ACADEMIC_YEAR,
      };
      if (subjectIdOptional) params.subjectId = subjectIdOptional;
      if (isSuperAdmin) params.instituteId = instituteId;
      const res = await api.get('/admin/reports/faculty-cycle/pdf', {
        params,
        responseType: 'blob',
      });
      const blob = res.data instanceof Blob
        ? res.data
        : new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const subjectCode = subjectIdOptional
        ? subjects.find((s) => s.subject_id === subjectIdOptional)?.subject_code
        : '';
      const emp = selectedFaculty?.employee_id || 'faculty';
      a.download = subjectCode
        ? `faculty-feedback-${emp}-sem${semester}-${subjectCode}.pdf`
        : `faculty-feedback-${emp}-sem${semester}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      let msg = 'Failed to download PDF';
      const data = requestError?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          msg = parsed?.error?.message || msg;
        } catch {
          /* keep default */
        }
      } else if (data?.error?.message) {
        msg = data.error.message;
      }
      setError(msg);
    } finally {
      setPdfDownloading(false);
    }
  };

  return (
    <div className="admin-wide-page">
      <section className="admin-master-panel">
        <div className="admin-master-panel-body">
          <div className="admin-master-header">
            <div>
              <h2 className="admin-master-title">View Faculty Feedback</h2>
              <p className="admin-master-subtitle">
                Academic year {OPERATIONAL_ACADEMIC_YEAR} — select a semester and faculty member to view subject-wise feedback progress.
              </p>
            </div>
            {cycle && (
              <span className={`faculty-cycle-badge ${cycle.cycle_status?.toLowerCase()}`}>
                Semester {cycle.semester} {cycle.cycle_status}
              </span>
            )}
          </div>

          <div className="faculty-feedback-filters">
            <label>
              <span className="input-label">Semester</span>
              <select
                value={semester}
                onChange={(event) => setSemester(event.target.value)}
                className="input select-single-line"
                disabled={semesterOptions.length === 0}
              >
                {semesterOptions.length === 0 && <option value="">No active semester</option>}
                {semesterOptions.map((number) => <option key={number} value={number}>Semester {number}</option>)}
              </select>
            </label>
            <label>
              <span className="input-label">Faculty</span>
              <select
                value={facultyId}
                onChange={(event) => {
                  setFacultyId(event.target.value);
                  setReport(null);
                }}
                className="input select-single-line"
                disabled={!semester || loadingFaculty}
              >
                <option value="">Select faculty</option>
                {facultyOptions.map((faculty) => (
                  <option key={faculty.id} value={faculty.id}>{facultyLabel(faculty)}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={viewFeedback}
              className="btn-primary"
              disabled={!facultyId || loadingFaculty || loadingFeedback}
            >
              {loadingFeedback ? 'Loading...' : 'View Feedback'}
            </button>
          </div>

          {error && <div className="alert-error mt-4">{error}</div>}
          {!instituteId && (
            <div className="admin-empty mt-5">
              <p className="admin-empty-title">Select an institute</p>
              <p className="admin-empty-text">Choose an institute from the header to load faculty feedback.</p>
            </div>
          )}
          {!error && instituteId && semester && !loadingFaculty && !cycle && (
            <div className="admin-empty mt-5">
              <p className="admin-empty-title">No feedback cycle found</p>
              <p className="admin-empty-text">
                There is no feedback cycle for {OPERATIONAL_ACADEMIC_YEAR}, Semester {semester}.
                Run promote-roster / feedback configuration for this semester.
              </p>
            </div>
          )}
          {!error && cycle && !loadingFaculty && facultyOptions.length === 0 && (
            <div className="admin-empty mt-5">
              <p className="admin-empty-title">No faculty allocations</p>
              <p className="admin-empty-text">No faculty members are allocated to subjects in this semester.</p>
            </div>
          )}
        </div>
      </section>

      {selectedFaculty && (
        <section className="admin-master-panel">
          <div className="admin-master-panel-body">
            <div className="admin-master-header">
              <div>
                <h3 className="admin-master-title">{facultyLabel(selectedFaculty)}</h3>
                <p className="admin-master-subtitle">Allocated subjects and student feedback status</p>
              </div>
              <button
                type="button"
                className="btn-secondary text-sm inline-flex items-center gap-2"
                disabled={pdfDownloading || subjects.length === 0}
                onClick={() => downloadFacultyFeedbackPdf()}
              >
                <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                {pdfDownloading ? 'Generating…' : 'Download PDF'}
              </button>
            </div>

            {subjects.length === 0 ? (
              <div className="admin-empty">
                <p className="admin-empty-title">No allocated subjects</p>
                <p className="admin-empty-text">This faculty member has no subject allocation for Semester {semester}.</p>
              </div>
            ) : (
              <div className="admin-master-table-wrap">
                <table className="admin-master-table faculty-feedback-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Division</th>
                      <th>Feedback Status</th>
                      <th>Responses</th>
                      <th>Average Rating</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map((subject) => {
                      const expected = Number(subject.expected_students || 0);
                      const received = Number(subject.total_responses || 0);
                      return (
                        <tr key={subject.subject_id}>
                          <td>
                            <span className="faculty-subject-code">{subject.subject_code}</span>
                            <span className="faculty-subject-name">{subject.subject_name}</span>
                          </td>
                          <td>{subject.divisions || '-'}</td>
                          <td><span className={`feedback-status ${String(subject.feedback_status).toLowerCase()}`}>{subject.feedback_status}</span></td>
                          <td>{received}{expected > 0 ? ` / ${expected}` : ''}</td>
                          <td className="font-semibold">{rating(subject.avg_overall_rating)}</td>
                          <td>
                            {subject.report_available ? (
                              <button type="button" onClick={() => setReport(subject)} className="btn-secondary text-xs">View Report</button>
                            ) : (
                              <span className="text-xs text-slate-500">
                                {subject.feedback_status === 'ACTIVE' ? 'In progress' : 'Report unavailable'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {report && selectedFaculty && (
        <section className="admin-master-panel" aria-label="Faculty feedback analysis report">
          <div className="admin-master-panel-body">
            <div className="admin-master-header">
              <div className="flex items-start gap-3">
                <span className="faculty-report-icon"><ChartBarSquareIcon className="h-5 w-5" /></span>
                <div>
                  <h3 className="admin-master-title">Feedback Summary and Analysis</h3>
                  <p className="admin-master-subtitle">{selectedFaculty.name} | {report.subject_code} {report.subject_name}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary text-sm inline-flex items-center gap-2"
                  disabled={pdfDownloading}
                  onClick={() => downloadFacultyFeedbackPdf(report.subject_id)}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                  Download PDF
                </button>
                <button type="button" onClick={() => setReport(null)} className="btn-secondary text-sm">Close Report</button>
              </div>
            </div>

            <div className="faculty-report-summary">
              <div><span>Overall rating</span><strong>{rating(report.avg_overall_rating)}</strong><small>out of 5</small></div>
              <div><span>Responses received</span><strong>{Number(report.approved_responses || 0)}</strong><small>approved responses</small></div>
              <div><span>Participation</span><strong>{Number(report.expected_students || 0) > 0 ? `${Math.min(100, Math.round((Number(report.total_responses || 0) / Number(report.expected_students)) * 100))}%` : '-'}</strong><small>{Number(report.total_responses || 0)} of {Number(report.expected_students || 0)} students</small></div>
            </div>

            <h4 className="faculty-analysis-title">Question and criterion analysis</h4>
            <div className="faculty-analysis-grid">
              {CRITERIA.map(([label, key]) => {
                const score = report[key] == null ? 0 : Number(report[key]);
                return (
                  <div key={key} className="faculty-analysis-row">
                    <div><span>{label}</span><strong>{report[key] == null ? '-' : score.toFixed(2)}</strong></div>
                    <div className="faculty-analysis-track"><span style={{ width: `${Math.max(0, Math.min(100, (score / 5) * 100))}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
